import { MapAreaService } from '@/modules/map-editor';
import type { TransferAnimationCallbacks, TransferTarget } from '@/modules/map-editor';

/** Duration of the transfer animation in milliseconds. */
const ANIMATION_DURATION_MS = 300;

/**
 * Easing function: ease-out cubic — starts fast, decelerates.
 *
 * @param t - Normalised progress in [0, 1]
 * @returns Eased value in [0, 1]
 */
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Service for animating map area transitions.
 *
 * Provides time-based, ease-out animations with external cancellation support.
 * Calling {@link transferMapArea} while an animation is running cancels the
 * previous one before starting the new animation.
 */
export class MapAreaAnimator {
  private mapAreaService: MapAreaService;

  /** Currently active `requestAnimationFrame` handle, or `null` when idle. */
  private animationFrameId: number | null = null;

  /** Stored `onCancel` callback for the active animation. */
  private activeOnCancel: (() => void) | undefined;

  constructor(mapAreaService: MapAreaService) {
    this.mapAreaService = mapAreaService;
  }

  /** Whether a transfer animation is currently running. */
  get isAnimating(): boolean {
    return this.animationFrameId !== null;
  }

  /**
   * Animate the map area to a new position.
   *
   * Two modes:
   * - **Centering** (`target` omitted or `null`): computes the delta needed to
   *   center the map in the viewport, then shifts by half that delta.
   * - **Shift** (`target` provided): animates by the exact `target.x` / `target.y` offset.
   *
   * Uses `performance.now()` for frame-rate-independent timing with ease-out cubic easing.
   * Positioning is computed as absolute offsets from a start snapshot to avoid
   * floating-point drift across frames.
   *
   * @param target - Shift coordinates, or `null`/`undefined` for centering mode.
   * @param callbacks - Optional lifecycle callbacks (`onStart`, `onComplete`, `onCancel`).
   */
  transferMapArea(
    target?: TransferTarget | null,
    callbacks?: TransferAnimationCallbacks
  ): void {
    // Cancel any in-flight animation before starting a new one
    this.cancelInternal(false);

    const pixelRatio = window.devicePixelRatio;
    const windowWidth = window.innerWidth * pixelRatio;
    const windowHeight = window.innerHeight * pixelRatio;

    const mapAreaState = this.mapAreaService.getState();

    // Compute total delta to animate
    let deltaX: number;
    let deltaY: number;

    if (target) {
      // Shift mode — animate by exact offset
      deltaX = target.x;
      deltaY = target.y;
    } else {
      // Centering mode — shift by half the overflow to center the map
      deltaX = (windowWidth - (mapAreaState.x * 2 + mapAreaState.width)) / 2;
      deltaY = (windowHeight - (mapAreaState.y * 2 + mapAreaState.height)) / 2;
    }

    // Snapshot start position for absolute positioning
    const startX = mapAreaState.x;
    const startY = mapAreaState.y;

    // Store the cancel callback for external cancellation
    this.activeOnCancel = callbacks?.onCancel;

    callbacks?.onStart?.();
    this.mapAreaService.updateMap(true);

    const startTime = performance.now();

    const animate = (now: number): void => {
      const elapsed = now - startTime;
      const rawProgress = Math.min(elapsed / ANIMATION_DURATION_MS, 1);
      const easedProgress = easeOutCubic(rawProgress);

      // Absolute position from snapshot — no accumulation drift
      this.mapAreaService.setCoordinates(
        startX + deltaX * easedProgress,
        startY + deltaY * easedProgress
      );

      if (rawProgress < 1) {
        this.animationFrameId = requestAnimationFrame(animate);
      } else {
        // Animation complete
        this.animationFrameId = null;
        this.activeOnCancel = undefined;
        this.mapAreaService.updateMap(false);
        callbacks?.onComplete?.();
      }
    };

    this.animationFrameId = requestAnimationFrame(animate);
  }

  /**
   * Cancel any in-flight transfer animation.
   * The map stays at its current interpolated position.
   * Invokes the `onCancel` callback if one was provided.
   */
  cancel(): void {
    this.cancelInternal(true);
  }

  /**
   * Internal cancellation helper.
   *
   * @param invokeCallback - Whether to fire the stored `onCancel` callback.
   *   Set to `false` when cancelling internally before starting a new animation.
   */
  private cancelInternal(invokeCallback: boolean): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
      this.mapAreaService.updateMap(false);

      if (invokeCallback) {
        this.activeOnCancel?.();
      }
      this.activeOnCancel = undefined;
    }
  }
}
